import { z } from "zod";
import { generateTraceId, TRACE_ID_HEADER } from "@/shared/traceId";

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/$/, "");

export const handleTokenExpiration = (): void => {
  if (typeof window !== "undefined") {
    // Redirect to login which will use BFF flow
    window.location.replace("/login");
  }
};

export function apiUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (normalized === API_BASE || normalized.startsWith(`${API_BASE}/`)) {
    return normalized;
  }
  return `${API_BASE}${normalized}`;
}

const apiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()),
});

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly traceId: string,
    readonly details: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

const envelopeBaseSchema = z.object({ success: z.boolean(), trace_id: z.string() });

async function readResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  const traceId = response.headers.get(TRACE_ID_HEADER) ?? "";

  if (!text.trim()) {
    throw new ApiClientError(
      response.status || 502,
      "EMPTY_RESPONSE",
      `Server returned an empty response (HTTP ${response.status || "unknown"})`,
      traceId,
      {},
    );
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new ApiClientError(
      response.status || 502,
      "INVALID_JSON",
      "Server returned a non-JSON response",
      traceId,
      { preview: text.slice(0, 200) },
    );
  }
}

/**
 * A 401 sends the browser to /login unless `redirectOnUnauthorized` is false:
 * a login-state probe reports 401 to its caller instead.
 */
export async function apiJson<T>(
  path: string,
  init: RequestInit = {},
  { redirectOnUnauthorized = true }: { redirectOnUnauthorized?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  headers.set(TRACE_ID_HEADER, generateTraceId());

  const response = await fetch(apiUrl(path), {
    ...init,
    headers,
  });
  const body = await readResponseBody(response);
  const base = envelopeBaseSchema.parse(body);

  if (!response.ok || !base.success) {
    const failed = z
      .object({
        success: z.literal(false),
        data: z.null(),
        error: apiErrorSchema,
        trace_id: z.string(),
      })
      .parse(body);
    if (response.status === 401 && redirectOnUnauthorized) {
      handleTokenExpiration();
    }
    throw new ApiClientError(
      response.status,
      failed.error.code,
      failed.error.message,
      failed.trace_id,
      failed.error.details,
    );
  }

  const success = z
    .object({
      success: z.literal(true),
      data: z.unknown(),
      error: z.null(),
      trace_id: z.string(),
    })
    .parse(body);
  return success.data as T;
}
